import { NavigationLinkOptions } from "@deere/fuel-react";
import { forwardRef, useContext, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router";
import { BusinessAction, RBACRole } from "../../../api/rbac/types";
import { ConfigContext } from "../../../context/ConfigContext";
import { FeatureToggleContextV2 } from "../../../context/FeatureToggleContextV2/FeatureToggleContextV2";
import { RBACAccessContext } from "../../../context/RBACAccessContext/RBACAccessContext";
import { SharedStateManagerContext } from "../../../context/SharedStateContext/SharedStateContext";
import { UserContext } from "../../../context/UserContext/UserContext";
import { CATEGORIES, TOGGLES } from "../../../pages/featureToggles/constants";
import { getHomeUrl } from "../../../utils/legacyHome";
import { getDisplayPromotions } from "../../../utils/promotions/promotionUtils";
import { getShowClaimStatus } from "../../../utils/showClaims";
import {
  isAllInvoicesEnabled,
  isConsolidatedInvoicesEnabled,
  isInvoicesEnabled,
} from "../../../utils/showInvoices";
import { isReturnsEnabled } from "../../../utils/showReturns";
import { ClaimsReturnsMenu } from "../components/Header/ClaimsReturnsMenu";
import D2DMenu from "../components/Header/D2DMenu";
import { OrderMenu } from "../components/Header/OrderMenu";
import { OrdersAndTrackingMenu } from "../components/Header/OrdersAndTrackingMenu";
import PartsPromotionsMenu from "../components/Header/PartsPromotionsMenu";
import { ReportsMenu } from "../components/Header/ReportsMenu";
import { ReturnsMenu } from "../components/Header/ReturnsMenu";
import RPMMenu from "../components/Header/RPMMenu";

type UseNavigationItems = (
  dealerAccountId: string | null,
  isUserCorporateManager: boolean,
  isCorporateManagerSetupIncomplete: boolean,
  displayRPM: boolean
) => {
  navigationItems: NavigationLinkOptions[];
};

type NavigationObject = NavigationLinkOptions & {
  comparison?: string[];
};

interface DisplayModuleMap {
  displayPromotions: boolean;
  displayPartInfo: boolean;
  displayOrders: boolean;
  displayClaimsInfo: boolean;
  displayInvoicesConsolidated: boolean;
  displayInvoicesForDealer: boolean;
  displayHandlingUnits: boolean;
  displayAllInvoices: boolean;
  displayReturns: boolean;
  displayMyDashboard: boolean;
  displayCreditStatus: boolean;
  displayCoreEligibilities: boolean;
  displayRPM: boolean;
  displayReports: boolean;
  displayD2D: boolean;
}

const getInitialNavigationItems = (
  homeUrl: string,
  dealerAccountId: string | null,
  enableCorporateView: boolean,
  isUserCorporateManager: boolean,
  isCorporateManagerSetupIncomplete: boolean,
  isValidJDUserWithAccount: boolean,
  displayModule: DisplayModuleMap,
  ENABLE_REMOVE_DASHBOARD_TAB: boolean,
  ENABLE_CLAIMS_AND_RETURNS_NAV: boolean,
  ENABLE_ORDERS_AND_TRACKING_MENU: boolean
): NavigationObject[] => {
  const myDashboardNavigationItem = {
    component: Link,
    to: "/my-dashboard",
    label: "header.navItem.myDashboard",
    comparison: ["/my-dashboard"],
  };

  const claimsNavigationItem = {
    component: Link,
    to: `/claims`,
    label: "header.navItem.claims",
    comparison: ["/claims"],
  };

  const invoicesNavigationItem = {
    component: Link,
    to: displayModule.displayInvoicesConsolidated
      ? `/invoices/download?accountId=${dealerAccountId}`
      : `/invoices`,
    label: "header.navItem.invoices",
    comparison: displayModule.displayAllInvoices
      ? ["/invoices", "/claim/create"]
      : ["/invoices"],
  };

  const orderNavigationItem = ENABLE_ORDERS_AND_TRACKING_MENU
    ? {
        // eslint-disable-next-line react/prop-types, @typescript-eslint/no-unused-vars
        component: forwardRef(({ selected }: { selected: boolean }, _ref) =>
          OrdersAndTrackingMenu({
            dealerAccountId: dealerAccountId as string,
            selected,
          })
        ),
        comparison: [`/orders`, `/credit-status`, `/handlingUnits`],
      }
    : {
        // eslint-disable-next-line react/prop-types, @typescript-eslint/no-unused-vars
        component: forwardRef(({ selected }: { selected: boolean }, _ref) =>
          OrderMenu({ dealerAccountId: dealerAccountId as string, selected })
        ),
        comparison: [`/orders`],
      };

  const returnsNavigationItem = displayModule.displayCoreEligibilities
    ? {
        // eslint-disable-next-line react/prop-types, @typescript-eslint/no-unused-vars
        component: forwardRef(({ selected }: { selected: boolean }, _ref) =>
          ReturnsMenu({ dealerAccountId: dealerAccountId as string, selected })
        ),
        comparison: ["/returns"],
      }
    : {
        component: Link,
        to: `/returns`,
        label: "header.navItem.returns",
        comparison: ["/returns"],
      };

  const d2dNavigationItem = {
    // eslint-disable-next-line react/prop-types, @typescript-eslint/no-unused-vars
    component: forwardRef(({ selected }: { selected: boolean }, _ref) =>
      D2DMenu({ dealerAccountId: dealerAccountId as string, selected })
    ),
    comparison: [
      `/d2d-requests`,
      `/d2d-orders-sell`,
      `/my-locator-inventory`,
      `/my-locator-entry`,
      `/other-dealer-inventory`,
      `/d2d-orders`,
      `/d2d-orders-buy`,
    ],
  };

  const defaultNavigationItems = ENABLE_REMOVE_DASHBOARD_TAB
    ? [
        {
          component: Link,
          to: "/home",
          label: "header.navItem.home",
          comparison: ["/home"],
        },
      ]
    : [
        {
          href: homeUrl,
          label: "header.navItem.home",
        },
        ...(displayModule.displayMyDashboard
          ? [myDashboardNavigationItem]
          : []),
      ];

  const rpmNavigationItem = {
    // eslint-disable-next-line react/prop-types, @typescript-eslint/no-unused-vars
    component: forwardRef(({ selected }: { selected: boolean }, _ref) =>
      RPMMenu({ selected })
    ),
    comparison: [`/dealer/${dealerAccountId}/rpm`],
  };

  const partsPromotionsNavigationItem = {
    // eslint-disable-next-line react/prop-types, @typescript-eslint/no-unused-vars
    component: forwardRef(({ selected }: { selected: boolean }, _ref) =>
      PartsPromotionsMenu({
        dealerAccountId: dealerAccountId as string,
        selected,
        showPartInfo: displayModule.displayPartInfo,
        showPromotion: displayModule.displayPromotions,
      })
    ),
    label:
      displayModule.displayPartInfo && displayModule.displayPromotions
        ? "header.navItem.partsPromotions"
        : "",
    comparison: ["/parts", "/promotions"],
  };

  const claimsReturnsNavigationItem = {
    // eslint-disable-next-line react/prop-types, @typescript-eslint/no-unused-vars
    component: forwardRef(({ selected }: { selected: boolean }, _ref) =>
      ClaimsReturnsMenu({
        dealerAccountId: dealerAccountId as string,
        selected,
        showClaim: displayModule.displayClaimsInfo,
        showReturns: displayModule.displayReturns,
        showCoreEligibilities: displayModule.displayCoreEligibilities,
      })
    ),
    label:
      displayModule.displayClaimsInfo || displayModule.displayReturns
        ? "header.navItem.claimsReturns"
        : "",
    comparison: ["/claims", "/returns"],
  };

  const handlingUnitNavigationItems = {
    component: Link,
    to: `/handlingUnits`,
    label: "header.navItem.handlingunits",
    comparison: ["/handlingUnits", "/claim/create-by-hu"],
  };

  const reportsNavigationItems = displayModule.displayReports
    ? {
        // eslint-disable-next-line react/prop-types, @typescript-eslint/no-unused-vars
        component: forwardRef(({ selected }: { selected: boolean }, _ref) =>
          ReportsMenu({ selected })
        ),
        comparison: ["/reports"],
      }
    : {
        component: Link,
        to: `/reports`,
        label: "header.navItem.reports",
        comparison: ["/reports"],
      };

  const creditStatusNavigationItem = {
    component: Link,
    to: `/credit-status`,
    label: "header.navItem.creditStatus",
    comparison: ["/credit-status"],
  };

  if (
    !isValidJDUserWithAccount ||
    (enableCorporateView &&
      isUserCorporateManager &&
      isCorporateManagerSetupIncomplete)
  ) {
    return defaultNavigationItems;
  }

  return [
    ...defaultNavigationItems,
    ...(!ENABLE_ORDERS_AND_TRACKING_MENU && displayModule.displayCreditStatus
      ? [creditStatusNavigationItem]
      : []),
    ...(displayModule.displayPartInfo || displayModule.displayPromotions
      ? [partsPromotionsNavigationItem]
      : []),
    ...(displayModule.displayOrders ? [orderNavigationItem] : []),
    ...(!ENABLE_ORDERS_AND_TRACKING_MENU && displayModule.displayHandlingUnits
      ? [handlingUnitNavigationItems]
      : []),
    ...(displayModule.displayInvoicesForDealer ? [invoicesNavigationItem] : []),
    ...((displayModule.displayReturns || displayModule.displayClaimsInfo) &&
    ENABLE_CLAIMS_AND_RETURNS_NAV
      ? [claimsReturnsNavigationItem]
      : [
          ...(displayModule.displayClaimsInfo ? [claimsNavigationItem] : []),
          ...(displayModule.displayReturns ? [returnsNavigationItem] : []),
        ]),
    ...(displayModule.displayRPM ? [rpmNavigationItem] : []),
    ...(displayModule.displayReports ? [reportsNavigationItems] : []),
    ...(displayModule.displayD2D ? [d2dNavigationItem] : []),
  ];
};

const getCurrentSelectedNavItemIndex = (
  navItems: NavigationObject[],
  location: string
): number =>
  navItems.findIndex(
    (item) =>
      (item.comparison &&
        location &&
        item.comparison.some((path) => location.includes(path))) ||
      (item.href && location.includes(item.href))
  );

const useNavigationItems: UseNavigationItems = (
  dealerAccountId,
  isUserCorporateManager,
  isCorporateManagerSetupIncomplete,
  displayRPM
) => {
  const {
    JDPO_JAVA_URL,
    JDPOINT_URL,
    ENABLE_HANDLING_UNITS,
    ENABLE_FEATURE_TOGGLE_CONTEXT_V2,
    ENABLE_CREDIT_STATUS,
    ENABLE_CLAIMS,
    ENABLE_RPM_FOR_JDPRISM_LINKS,
    PPT_REPORTS_BACKEND_URL,
    ENABLE_REPORTS,
    ENABLE_REMOVE_DASHBOARD_TAB,
    ENABLE_CLAIMS_AND_RETURNS_NAV,
    ENABLE_ORDERS_AND_TRACKING_MENU,
    ENABLE_JDPS_POSTSHIP_ACTIVITIES,
    ENABLE_JDPS_SURPLUS_RETURN_ACTIVITIES,
    ENABLE_D2D,
  } = useContext(ConfigContext);
  const { isRBACEnabled, isCorporateViewEnabled } =
    useContext(RBACAccessContext);
  const sharedStateManager = useContext(SharedStateManagerContext);
  const { isToggleEnabled } = useContext(FeatureToggleContextV2);
  const { ENABLE_CORE_ELIGIBILITIES } = useContext(ConfigContext);
  const user = useContext(UserContext);

  function isFeatureToggleEnabled(): boolean {
    return ENABLE_FEATURE_TOGGLE_CONTEXT_V2;
  }

  function isRbacEnabled(): boolean {
    return isRBACEnabled;
  }

  function featureToggleCheck(category: string, toggle: string): boolean {
    if (!isFeatureToggleEnabled()) return true;
    return isToggleEnabled(category, toggle);
  }

  function rbacAccessCheck(businessAction: BusinessAction): boolean {
    if (!isRbacEnabled()) return true;
    return !!user?.isAuthorizedTo(businessAction);
  }

  const isValidJDUserWithAccount = useMemo(
    () =>
      !isRbacEnabled() ||
      (isRbacEnabled() && user?.isUserWithJDRole() && !!dealerAccountId) ||
      (isRbacEnabled() && !user?.isUserWithJDRole()),
    [dealerAccountId]
  );

  function canShowClaims(): boolean {
    if (!ENABLE_CLAIMS) return false;
    if (!rbacAccessCheck(BusinessAction.VIEW_CLAIMS)) return false;
    if (user?.is(RBACRole.JDPS_SERVICE_DEALER)) {
      if (
        isFeatureToggleEnabled() &&
        !isToggleEnabled(CATEGORIES.CLAIMS_SERVICE, TOGGLES.ENABLE_CLAIMS)
      ) {
        return false;
      }
      return (
        !ENABLE_JDPS_POSTSHIP_ACTIVITIES || isJdpsPostshipActivitiesEnabled()
      );
    }
    if (isFeatureToggleEnabled())
      return isToggleEnabled(CATEGORIES.CLAIMS_SERVICE, TOGGLES.ENABLE_CLAIMS);
    return isRbacEnabled() || getShowClaimStatus();
  }

  function canShowInvoices() {
    if (!isRbacEnabled()) return isInvoicesEnabled();
    if (user?.is(RBACRole.JDPS_SERVICE_DEALER)) {
      if (
        isFeatureToggleEnabled() &&
        !isToggleEnabled(CATEGORIES.INVOICE_SERVICE, TOGGLES.ENABLE_INVOICES)
      ) {
        return false;
      }
      return (
        isJdpsPostshipActivitiesEnabled() &&
        rbacAccessCheck(BusinessAction.VIEW_INVOICES)
      );
    }
    return (
      rbacAccessCheck(BusinessAction.VIEW_INVOICES) &&
      featureToggleCheck(CATEGORIES.INVOICE_SERVICE, TOGGLES.ENABLE_INVOICES)
    );
  }

  function canShowConsolidatedInvoicesPage() {
    return !isRbacEnabled() && isConsolidatedInvoicesEnabled();
  }

  const location = useLocation();
  const { t } = useTranslation("common");

  function canShowCreditStatusMenu(): boolean {
    if (!ENABLE_CREDIT_STATUS) return false;
    if (!rbacAccessCheck(BusinessAction.VIEW_CREDIT_STATUS)) return false;
    return featureToggleCheck(CATEGORIES.EXTREME_UI, TOGGLES.CREDIT_STATUS);
  }

  function canDisplayPromotions() {
    if (!rbacAccessCheck(BusinessAction.VIEW_PROMOTIONS)) return false;
    if (isFeatureToggleEnabled())
      return isToggleEnabled(CATEGORIES.PROMOTIONS, TOGGLES.ENABLE_PROMOTIONS);
    return getDisplayPromotions();
  }

  function getEnablePartInfo() {
    if (!rbacAccessCheck(BusinessAction.VIEW_PART_INFORMATION)) return false;
    return featureToggleCheck(CATEGORIES.EXTREME_UI, TOGGLES.ENABLE_PARTS_INFO);
  }

  function getEnableOrders() {
    if (
      !rbacAccessCheck(BusinessAction.VIEW_ORDERS) &&
      !rbacAccessCheck(BusinessAction.CREATE_ORDER) &&
      !rbacAccessCheck(BusinessAction.EDIT_ORDER)
    )
      return false;
    return featureToggleCheck(CATEGORIES.EXTREME_UI, TOGGLES.ENABLE_ORDERS);
  }

  function getEnableHandlingUnits() {
    if (!ENABLE_HANDLING_UNITS) return false;
    if (!rbacAccessCheck(BusinessAction.VIEW_HANDLING_UNITS)) return false;
    if (user?.is(RBACRole.JDPS_SERVICE_DEALER)) {
      if (!isFeatureToggleEnabledForHUs()) return false;
      return (
        !ENABLE_JDPS_POSTSHIP_ACTIVITIES || isJdpsPostshipActivitiesEnabled()
      );
    }
    if (!isFeatureToggleEnabledForHUs()) return false;
    return isRbacEnabled() || getShowClaimStatus();
  }

  function getEnableReports() {
    if (!ENABLE_REPORTS) return false;
    if (!PPT_REPORTS_BACKEND_URL) return false;
    const hasPromotionTrackerRole = user?.is(RBACRole.PROMOTION_TRACKER);
    if (!hasPromotionTrackerRole) return false;
    if (!isFeatureToggleEnabledForReports()) return false;
    return isRbacEnabled();
  }

  function isFeatureToggleEnabledForHUs() {
    return ENABLE_FEATURE_TOGGLE_CONTEXT_V2 && isRbacEnabled()
      ? isToggleEnabled(
          CATEGORIES.HANDLING_UNIT_SERVICE,
          TOGGLES.ENABLE_HANDLING_UNITS
        )
      : true;
  }

  function isFeatureToggleEnabledForReports() {
    return ENABLE_FEATURE_TOGGLE_CONTEXT_V2 && isRbacEnabled()
      ? isToggleEnabled(CATEGORIES.EXTREME_UI, TOGGLES.ENABLE_REPORTS)
      : true;
  }

  function getEnableCoreEligibilities() {
    if (!ENABLE_CORE_ELIGIBILITIES) return false;
    if (!rbacAccessCheck(BusinessAction.VIEW_ELIGIBILITIES)) return false;
    if (!isFeatureToggleEnabledForEligibilities()) return false;
    return isRbacEnabled();
  }

  function isFeatureToggleEnabledForEligibilities() {
    return ENABLE_FEATURE_TOGGLE_CONTEXT_V2 && isRbacEnabled()
      ? isToggleEnabled(
          CATEGORIES.CORE_ELIGIBILITIES_SERVICE,
          TOGGLES.ENABLE_CORE_ELIGIBILITIES
        )
      : true;
  }

  function getEnableRPM() {
    return (
      ENABLE_FEATURE_TOGGLE_CONTEXT_V2 &&
      isRbacEnabled() &&
      ENABLE_RPM_FOR_JDPRISM_LINKS &&
      displayRPM &&
      isToggleEnabled(CATEGORIES.EXTREME_UI, TOGGLES.ENABLE_RPM_FOR_JDPRISM)
    );
  }

  function getEnableReturns() {
    if (user?.is(RBACRole.JDPS_SERVICE_DEALER)) {
      if (!isFeatureToggleEnabledForReturns()) return false;
      return (
        isJdpsPostshipActivitiesEnabled() ||
        isJdpsSurplusReturnActivitiesEnabled()
      );
    }
    if (!rbacAccessCheck(BusinessAction.VIEW_RETURNS)) return false;
    if (!isFeatureToggleEnabledForReturns()) return false;
    return isRbacEnabled() || isReturnsEnabled();
  }

  function isJdpsPostshipActivitiesEnabled() {
    return (
      ENABLE_JDPS_POSTSHIP_ACTIVITIES &&
      isToggleEnabled(
        CATEGORIES.RETURNS,
        TOGGLES.ENABLE_JDPS_POSTSHIP_ACTIVITIES
      )
    );
  }

  function isJdpsSurplusReturnActivitiesEnabled() {
    return (
      isToggleEnabled(CATEGORIES.RETURNS, TOGGLES.ENABLE_SURPLUS_RETURNS) &&
      ENABLE_JDPS_SURPLUS_RETURN_ACTIVITIES &&
      isToggleEnabled(
        CATEGORIES.RETURNS,
        TOGGLES.ENABLE_JDPS_SURPLUS_RETURN_ACTIVITIES
      )
    );
  }

  function isFeatureToggleEnabledForReturns() {
    return isRbacEnabled()
      ? isToggleEnabled(CATEGORIES.RETURNS, TOGGLES.ENABLE_RETURNS)
      : true;
  }

  function getEnableMyDashboard() {
    return rbacAccessCheck(BusinessAction.VIEW_MY_DASHBOARD);
  }

  function isFeatureToggleEnabledForD2D() {
    const isDynamicToggleEnabled = ENABLE_FEATURE_TOGGLE_CONTEXT_V2
      ? isToggleEnabled(CATEGORIES.D2D_SERVICE, TOGGLES.ENABLE_D2D)
      : true;
    return ENABLE_D2D && isDynamicToggleEnabled;
  }
  const homeUrl = getHomeUrl(
    JDPO_JAVA_URL,
    JDPOINT_URL,
    dealerAccountId,
    ENABLE_REMOVE_DASHBOARD_TAB
  );

  const displayOrders = ENABLE_ORDERS_AND_TRACKING_MENU
    ? getEnableOrders() || canShowCreditStatusMenu() || getEnableHandlingUnits()
    : getEnableOrders();

  const navItems = getInitialNavigationItems(
    homeUrl,
    dealerAccountId,
    isCorporateViewEnabled,
    isUserCorporateManager,
    isCorporateManagerSetupIncomplete,
    isValidJDUserWithAccount,
    {
      displayPromotions: canDisplayPromotions(),
      displayPartInfo: getEnablePartInfo(),
      displayOrders,
      displayClaimsInfo: canShowClaims(),
      displayInvoicesConsolidated: canShowConsolidatedInvoicesPage(),
      displayInvoicesForDealer: canShowInvoices(),
      displayHandlingUnits: getEnableHandlingUnits(),
      displayAllInvoices: isAllInvoicesEnabled(),
      displayReturns: getEnableReturns(),
      displayMyDashboard: getEnableMyDashboard(),
      displayCreditStatus: canShowCreditStatusMenu(),
      displayCoreEligibilities: getEnableCoreEligibilities(),
      displayRPM: getEnableRPM(),
      displayReports: getEnableReports(),
      displayD2D: isFeatureToggleEnabledForD2D(),
    },
    ENABLE_REMOVE_DASHBOARD_TAB,
    ENABLE_CLAIMS_AND_RETURNS_NAV,
    ENABLE_ORDERS_AND_TRACKING_MENU
  );

  const translatedNavItems = navItems.map((item) => ({
    ...item,
    label: t(`${item.label}`),
  }));

  const { currentSelectedNavigationItemIndex } = sharedStateManager.state;

  useEffect(() => {
    const currentSelected = getCurrentSelectedNavItemIndex(
      translatedNavItems,
      location.pathname
    );
    if (currentSelected !== currentSelectedNavigationItemIndex) {
      sharedStateManager.setState({
        ...sharedStateManager.state,
        currentSelectedNavigationItemIndex: currentSelected,
      });
    }
  }, [location.pathname, translatedNavItems.length]);

  const navigationItems = Array.from(translatedNavItems, (item, itemIndex) => {
    const isItemSelected =
      itemIndex === sharedStateManager.state.currentSelectedNavigationItemIndex;
    return {
      ...item,
      selected: isItemSelected,
      onClick: (): void => {
        sharedStateManager.setState({
          ...sharedStateManager.state,
          currentSelectedNavigationItemIndex: itemIndex,
          previousSelectedNavigationItemIndex:
            sharedStateManager.state.currentSelectedNavigationItemIndex,
        });
      },
    };
  });

  return { navigationItems };
};

export default useNavigationItems;
